/**
 * Client Supabase typé.
 *
 * Le client est créé **paresseusement** : Villopia doit démarrer et être jouable
 * en moins de 5 secondes sans compte et sans réseau (§1). Aucun appel réseau, ni
 * même l'instanciation du client, ne doit se produire au chargement du module.
 * Le jeu vit d'abord en localStorage ; Supabase n'entre en scène qu'au moment de
 * la synchronisation ou du partage (Phase 2, étape 10).
 *
 * Authentification : le projet active les **connexions anonymes**. Un joueur sans
 * compte obtient tout de même un `auth.uid()`, ce qui permet aux policies RLS de
 * rester `owner_id = auth.uid()` et rend l'ajout ultérieur d'un e-mail ou d'un
 * compte Google natif, sans migration de données. Voir la migration initiale.
 */

import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

export type VillopiaClient = SupabaseClient<Database>;

export type CityRow = Database['public']['Tables']['cities']['Row'];
export type BoostRow = Database['public']['Tables']['boosts']['Row'];
export type DailyStatsRow = Database['public']['Tables']['daily_stats']['Row'];

/**
 * Les seules colonnes de `cities` qu'un client a le droit d'écrire.
 *
 * `total_production`, `prestige_level` et `slug` sont volontairement absents :
 * la base refuse leur écriture par privilège de colonne (voir la migration
 * initiale), afin que les classements des ligues ne soient pas forgeables depuis
 * la console du navigateur. Ce type rend cette frontière visible à la compilation
 * plutôt qu'à l'exécution, sous la forme d'une erreur 42501.
 */
export type CityClientUpdate = Pick<
  Database['public']['Tables']['cities']['Update'],
  'name' | 'snapshot'
>;

export type CityClientInsert = Pick<
  Database['public']['Tables']['cities']['Insert'],
  'owner_id' | 'name' | 'snapshot'
>;

/** Clé de stockage de la session, préfixée pour ne pas entrer en conflit avec la save du jeu. */
const AUTH_STORAGE_KEY = 'villopia.auth.v1';

interface SupabaseConfig {
  readonly url: string;
  readonly anonKey: string;
}

function readConfig(): SupabaseConfig | null {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (url === undefined || url === '' || anonKey === undefined || anonKey === '') {
    return null;
  }

  return { url, anonKey };
}

/**
 * Indique si le jeu dispose d'une configuration Supabase utilisable.
 * Permet à l'UI de masquer partage, compte et classements plutôt que de proposer
 * des boutons qui échoueront — utile aussi pour jouer en local sans back-end.
 */
export function isSupabaseConfigured(): boolean {
  return readConfig() !== null;
}

let client: VillopiaClient | null = null;

/**
 * Retourne le client partagé, en le créant au premier appel.
 * @throws si les variables d'environnement Supabase sont absentes — l'appelant
 *         doit avoir vérifié `isSupabaseConfigured()` au préalable.
 */
export function getSupabase(): VillopiaClient {
  if (client !== null) {
    return client;
  }

  const config = readConfig();
  if (config === null) {
    throw new Error(
      'Configuration Supabase absente : renseigne VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY dans .env (voir .env.example).',
    );
  }

  client = createClient<Database>(config.url, config.anonKey, {
    auth: {
      storageKey: AUTH_STORAGE_KEY,
      persistSession: true,
      autoRefreshToken: true,
      // Nécessaire pour finaliser un magic link ou un retour OAuth (Phase 2, étape 10).
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
    global: {
      headers: { 'x-application-name': 'villopia' },
    },
  });

  return client;
}

/**
 * Une seule ouverture de session à la fois.
 *
 * Deux appelants concurrents — la synchronisation et le registre des gemmes le font
 * au même démarrage — déclencheraient deux `signInAnonymously`, donc deux comptes
 * anonymes, donc deux villes. La promesse en vol est partagée : le second appelant
 * attend le premier au lieu d'en créer un second.
 */
let pendingSession: Promise<string | null> | null = null;

/**
 * L'identité DÉJÀ acquise, ou `null`. **N'en crée jamais.**
 *
 * ══ Pourquoi cette porte existe à côté de `ensureAuthSession` ═══════════════
 *
 * ⚠️ `ensureAuthSession()` MINTE un compte anonyme quand il n'y en a pas. C'est ce qu'on
 * veut pour sauvegarder une ville — et pas du tout ce qu'on veut pour mesurer.
 *
 * La mesure d'audience appelait `ensureAuthSession()` au démarrage, pour attacher un
 * identifiant à ses événements. Conséquence : **chaque ouverture du jeu créait un compte
 * anonyme**, avant tout geste du joueur, y compris s'il repartait aussitôt. Le verrou
 * posé sur la synchro (1.4.0) n'y changeait rien — la synchro n'était pas la coupable.
 *
 * Mesuré par `probe:sync` : un `signup` partait sur une ville vide, verrou en place.
 *
 * La distinction est de fond, pas technique : **analyser, c'est reconnaître quelqu'un qui
 * est là ; ce n'est pas l'inscrire.** Un joueur qui n'a pas encore de compte se mesure
 * anonymement, ce que PostHog fait très bien — `startAnalytics(null)` part de toute façon.
 */
export async function currentAuthSession(): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  try {
    const { data } = await getSupabase().auth.getSession();
    return data.session?.user.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Garantit que le joueur a une identité, et rend son UUID.
 *
 * Rend `null` sans configuration Supabase, ou si l'ouverture échoue — le cas est
 * réel et fréquent : la limite de trente connexions anonymes par heure et par IP,
 * qu'une campagne de sondes suffit à saturer. L'appelant doit donc traiter `null`
 * comme « pas d'identité pour l'instant », jamais comme une panne.
 *
 * ⚠️ Cette fonction est la SEULE porte d'entrée vers une identité. Elle vivait dans
 * `sync.ts` ; le registre des gemmes en avait besoin lui aussi, et un second
 * `signInAnonymously` recopié ailleurs aurait fini par ouvrir deux comptes au même
 * joueur.
 */
export async function ensureAuthSession(): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  if (pendingSession !== null) return pendingSession;

  pendingSession = (async (): Promise<string | null> => {
    try {
      const supabase = getSupabase();

      const { data } = await supabase.auth.getSession();
      const existing = data.session?.user.id;
      if (existing !== undefined) return existing;

      // Pas de session : le joueur reçoit une identité anonyme, persistée localement.
      // C'est elle qui deviendra son compte s'il attache un e-mail plus tard.
      const { data: anon, error } = await supabase.auth.signInAnonymously();
      if (error !== null) return null;
      return anon.user?.id ?? null;
    } catch {
      return null;
    } finally {
      // La promesse ne se garde PAS : une tentative ratée doit pouvoir être rejouée
      // au prochain appel, sinon un 429 passager condamnerait toute la session.
      pendingSession = null;
    }
  })();

  return pendingSession;
}

/** Réinitialise le client — réservé aux tests. */
export function resetSupabaseClientForTests(): void {
  client = null;
  pendingSession = null;
}
